<script setup lang="ts">
/**
 * AppNav — shared top navigation for the mobile-first dashboard.
 *
 * Renders four primary destinations, grouped analysis/tools destinations, and
 * a settings button (gear) for theme and font-size preferences. Popovers close
 * on route change, outside click, or Escape.
 *
 * The container wraps instead of scrolling horizontally: on narrow viewports
 * the brand switcher and settings gear would otherwise sit past the clipped
 * overflow edge with no scrollbar cue (bead lucky-war-tdvy). Wrapping keeps
 * every control visible and touch-reachable at any width/font scale; wide
 * screens still fit on one row (the spacer pins the gear to the right).
 *
 * Mobile (≤720px): the link row collapses behind a hamburger button; the
 * drawer stacks the route links + brand switcher vertically and closes on
 * route change, Escape, outside click, or crossing back to desktop width.
 *
 * bead: lucky-war-kwgf (nav); settings popover added with the adjustable-UI
 * feature (commit 92481c8) — see useTheme.ts.
 */
import { ref, reactive, nextTick, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTheme, FONT_SCALE_PRESETS } from '@/composables/useTheme';
import { BRAND_LENSES, lensFromQuery, brandQuery, type BrandLens } from '@/lib/brand';

const primaryLinks = [
  { key: 'home', to: '/', label: 'หน้าแรก', routes: ['home'] },
  { key: 'branch', to: '/branch', label: 'สาขา', routes: ['branch-list', 'branch', 'compare'] },
  { key: 'historical', to: '/historical', label: 'ย้อนหลัง', routes: ['historical', 'weekly', 'weekly-week'] },
];
const analysisLinks = [
  { to: '/social', label: 'โซเชียล' },
  { to: '/competitive-analysis', label: 'วิเคราะห์คู่แข่ง' },
];
const toolLinks = [{ to: '/ops', label: 'สถานะระบบ' }];

const { theme, setTheme, fontScale, setFontScale } = useTheme();
const route = useRoute();
const router = useRouter();
const brand = computed<BrandLens>(() => lensFromQuery(route.query.brand));
const showBrandSwitcher = computed(() => route.name !== 'branch');
const currentRouteName = computed(() => String(route.name ?? ''));
const analysisActive = computed(() => ['discovery', 'social', 'competitive-analysis'].includes(currentRouteName.value));
const toolsActive = computed(() => currentRouteName.value === 'ops');
const analysisCurrentLabel = computed(() => ({
  discovery: 'วิเคราะห์',
  social: 'โซเชียล',
  'competitive-analysis': 'วิเคราะห์คู่แข่ง',
}[currentRouteName.value] ?? ''));

function primaryActive(routes: string[]): boolean {
  return routes.includes(currentRouteName.value);
}

function setBrand(value: BrandLens) {
  const query = { ...route.query };
  const next = brandQuery(value);
  if (next) query.brand = next;
  else delete query.brand;
  router.replace({ query });
}

function routeWithBrand(to: string) {
  return { path: to, query: route.query.brand ? { brand: route.query.brand } : undefined };
}

watch(() => route.query.brand, (value) => {
  // Invalid values are normalized once, without adding a history entry.
  if (value != null && value !== 'suki' && value !== 'bbq') setBrand('all');
});

// ── Desktop grouped navigation ───────────────────────────────────────────────
const analysisOpen = ref(false);
const toolsOpen = ref(false);
const analysisBtn = ref<HTMLElement | null>(null);
const toolsBtn = ref<HTMLElement | null>(null);
const analysisPanel = ref<HTMLElement | null>(null);
const toolsPanel = ref<HTMLElement | null>(null);

function toggleAnalysisMenu() {
  analysisOpen.value = !analysisOpen.value;
  toolsOpen.value = false;
  settingsOpen.value = false;
}

function toggleToolsMenu() {
  toolsOpen.value = !toolsOpen.value;
  analysisOpen.value = false;
  settingsOpen.value = false;
}

function closeGroupedMenus() {
  analysisOpen.value = false;
  toolsOpen.value = false;
}

async function closeGroupedMenusAndRestoreFocus() {
  const focusTarget = analysisOpen.value ? analysisBtn.value : toolsOpen.value ? toolsBtn.value : null;
  closeGroupedMenus();
  await nextTick();
  focusTarget?.focus();
}

// ── Mobile hamburger drawer state ────────────────────────────────────────────
const menuOpen = ref(false);
const menuBtn = ref<HTMLElement | null>(null);
const menuPanel = ref<HTMLElement | null>(null);
const drawerTop = ref(0);

function computeDrawerTop() {
  const header = menuBtn.value?.closest('.app-header') ?? menuBtn.value?.closest('.appnav');
  drawerTop.value = header?.getBoundingClientRect().bottom ?? 0;
}

function toggleMenu() {
  if (!menuOpen.value) computeDrawerTop();
  menuOpen.value = !menuOpen.value;
}

async function closeMenuAndRestoreFocus() {
  menuOpen.value = false;
  await nextTick();
  menuBtn.value?.focus();
}

async function onDrawerLinkClick() {
  menuOpen.value = false;
  await nextTick();
  document.getElementById('main-content')?.focus();
}

async function onGroupedLinkClick() {
  closeGroupedMenus();
  await nextTick();
  document.getElementById('main-content')?.focus();
}

// Navigation from the drawer should close it.
watch(() => route.fullPath, () => {
  menuOpen.value = false;
  closeGroupedMenus();
});

// ── Settings popover state ──────────────────────────────────────────────────
// The popover is teleported to <body> with position: fixed so it renders above
// the sticky header regardless of stacking context. Position is recomputed
// from the button's getBoundingClientRect on open, resize, and scroll.
const settingsOpen = ref(false);
const settingsBtn = ref<HTMLElement | null>(null);
const settingsPanel = ref<HTMLElement | null>(null);
const popoverPos = reactive({ top: -9999, right: -9999 });

function computePopoverPos() {
  const btn = settingsBtn.value;
  if (!btn) return;
  const r = btn.getBoundingClientRect();
  popoverPos.top = r.bottom + 4;
  popoverPos.right = Math.max(8, window.innerWidth - r.right);
}

async function openSettings() {
  settingsOpen.value = true;
  await nextTick();
  computePopoverPos();
}

function toggleSettings() {
  closeGroupedMenus();
  menuOpen.value = false;
  if (settingsOpen.value) {
    settingsOpen.value = false;
  } else {
    openSettings();
  }
}

function closeSettings() {
  settingsOpen.value = false;
}

function onDocClick(e: MouseEvent) {
  const target = e.target as Node | null;
  if (!target) return;
  if (
    analysisOpen.value &&
    !analysisBtn.value?.contains(target) &&
    !analysisPanel.value?.contains(target)
  ) {
    analysisOpen.value = false;
  }
  if (
    toolsOpen.value &&
    !toolsBtn.value?.contains(target) &&
    !toolsPanel.value?.contains(target)
  ) {
    toolsOpen.value = false;
  }
  if (
    settingsOpen.value &&
    !settingsBtn.value?.contains(target) &&
    !settingsPanel.value?.contains(target)
  ) {
    closeSettings();
  }
  if (
    menuOpen.value &&
    !menuBtn.value?.contains(target) &&
    !menuPanel.value?.contains(target)
  ) {
    menuOpen.value = false;
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key !== 'Escape') return;
  if (settingsOpen.value) closeSettings();
  if (menuOpen.value) closeMenuAndRestoreFocus();
  if (analysisOpen.value || toolsOpen.value) closeGroupedMenusAndRestoreFocus();
}

// Crossing back to desktop width dismisses the drawer (the inline row takes
// over). matchMedia is optional-guarded for jsdom, which does not implement it.
const desktopMq = typeof window.matchMedia === 'function'
  ? window.matchMedia('(min-width: 721px)')
  : null;
function onDesktopMq(e: MediaQueryListEvent) {
  if (e.matches) menuOpen.value = false;
}

function onViewportResize() {
  computePopoverPos();
  if (menuOpen.value) computeDrawerTop();
}

onMounted(() => {
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKeydown);
  window.addEventListener('resize', onViewportResize);
  window.addEventListener('scroll', computePopoverPos, true);
  desktopMq?.addEventListener('change', onDesktopMq);
});
onUnmounted(() => {
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onKeydown);
  window.removeEventListener('resize', onViewportResize);
  window.removeEventListener('scroll', computePopoverPos, true);
  desktopMq?.removeEventListener('change', onDesktopMq);
});

function pickFont(v: number) {
  setFontScale(v);
}
</script>

<template>
  <nav class="appnav" aria-label="เมนูหลัก">
    <button
      ref="menuBtn"
      type="button"
      class="appnav__icon-btn appnav__burger"
      :class="{ 'appnav__icon-btn--active': menuOpen }"
      :aria-expanded="menuOpen"
      :aria-controls="menuOpen ? 'appnav-drawer' : undefined"
      :aria-label="menuOpen ? 'ปิดเมนู' : 'เปิดเมนู'"
      :title="menuOpen ? 'ปิดเมนู' : 'เปิดเมนู'"
      @click="toggleMenu"
    >
      <!-- hamburger icon -->
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    </button>

    <div class="appnav__inline">
      <RouterLink
        v-for="l in primaryLinks"
        :key="l.key"
        :to="routeWithBrand(l.to)"
        class="appnav__link"
        :class="{ 'appnav__link--active': primaryActive(l.routes) }"
        :aria-current="primaryActive(l.routes) ? 'location' : undefined"
      >{{ l.label }}</RouterLink>

      <div class="appnav__group">
        <RouterLink
          :to="routeWithBrand('/discovery')"
          class="appnav__link appnav__group-main"
          :class="{ 'appnav__link--active': analysisActive }"
        >วิเคราะห์</RouterLink>
        <button
          ref="analysisBtn"
          type="button"
          class="appnav__group-toggle"
          :class="{ 'appnav__group-toggle--active': analysisActive || analysisOpen }"
          :aria-expanded="analysisOpen"
          :aria-current="analysisActive ? 'location' : undefined"
          aria-controls="appnav-analysis-menu"
          :aria-label="analysisActive ? `เปิดเมนูวิเคราะห์ หน้าปัจจุบัน: ${analysisCurrentLabel}` : 'เปิดเมนูวิเคราะห์'"
          @click="toggleAnalysisMenu"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div
          v-show="analysisOpen"
          id="appnav-analysis-menu"
          ref="analysisPanel"
          class="appnav__group-panel"
          aria-label="เมนูวิเคราะห์"
        >
          <RouterLink
            v-for="l in analysisLinks"
            :key="l.to"
            :to="routeWithBrand(l.to)"
            class="appnav__group-link"
            @click="onGroupedLinkClick"
          >{{ l.label }}</RouterLink>
        </div>
      </div>

      <div class="appnav__group appnav__group--tools">
        <button
          ref="toolsBtn"
          type="button"
          class="appnav__tools-btn"
          :class="{ 'appnav__tools-btn--active': toolsActive || toolsOpen }"
          :aria-expanded="toolsOpen"
          :aria-current="toolsActive ? 'location' : undefined"
          aria-controls="appnav-tools-menu"
          :aria-label="toolsActive ? 'เปิดเมนูเครื่องมือ หน้าปัจจุบัน: สถานะระบบ' : 'เปิดเมนูเครื่องมือ'"
          @click="toggleToolsMenu"
        >
          เครื่องมือ
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div
          v-show="toolsOpen"
          id="appnav-tools-menu"
          ref="toolsPanel"
          class="appnav__group-panel appnav__group-panel--right"
          aria-label="เมนูเครื่องมือ"
        >
          <RouterLink
            v-for="l in toolLinks"
            :key="l.to"
            :to="routeWithBrand(l.to)"
            class="appnav__group-link"
            @click="onGroupedLinkClick"
          >{{ l.label }}</RouterLink>
        </div>
      </div>

      <div v-if="showBrandSwitcher" class="appnav__brand" role="group" aria-label="เลือกแบรนด์">
        <button
          v-for="option in BRAND_LENSES"
          :key="option.value"
          type="button"
          class="appnav__brand-btn"
          :class="{ 'appnav__brand-btn--active': brand === option.value }"
          :aria-pressed="brand === option.value"
          @click="setBrand(option.value)"
        >{{ option.label }}</button>
      </div>
    </div>

    <div class="appnav__spacer"></div>

    <div class="appnav__settings">
      <button
        ref="settingsBtn"
        type="button"
        class="appnav__icon-btn appnav__gear"
        :class="{ 'appnav__icon-btn--active': settingsOpen }"
        :aria-expanded="settingsOpen"
        aria-haspopup="true"
        aria-label="ตั้งค่าหน้าจอ"
        title="ตั้งค่าหน้าจอ"
        @click="toggleSettings"
      >
        <!-- gear icon -->
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      <Teleport to="body">
        <div
          v-if="settingsOpen"
          ref="settingsPanel"
          class="settings"
          :style="{ top: `${popoverPos.top}px`, right: `${popoverPos.right}px` }"
          role="dialog"
          aria-label="ตั้งค่าหน้าจอ"
        >
          <div class="settings__row">
            <span class="settings__label">ธีม</span>
            <div class="settings__seg" role="group" aria-label="เลือกธีม">
              <button
                type="button"
                class="settings__seg-btn"
                :class="{ 'settings__seg-btn--active': theme === 'dark' }"
                @click="setTheme('dark')"
              >มืด</button>
              <button
                type="button"
                class="settings__seg-btn"
                :class="{ 'settings__seg-btn--active': theme === 'light' }"
                @click="setTheme('light')"
              >สว่าง</button>
            </div>
          </div>

          <div class="settings__row">
            <span class="settings__label">ขนาดตัวอักษร</span>
            <div class="settings__seg" role="group" aria-label="เลือกขนาดตัวอักษร">
              <button
                v-for="p in FONT_SCALE_PRESETS"
                :key="p.value"
                type="button"
                class="settings__seg-btn"
                :class="{ 'settings__seg-btn--active': Math.abs(fontScale - p.value) < 0.005 }"
                :style="{ fontSize: `${0.7 * p.value}rem` }"
                @click="pickFont(p.value)"
              >{{ p.label }}</button>
            </div>
          </div>
        </div>
      </Teleport>
    </div>

    <!-- Mobile drawer (≤720px): stacked links + brand switcher. -->
    <div
      v-if="menuOpen"
      id="appnav-drawer"
      ref="menuPanel"
      class="appnav__drawer"
      :style="{ top: `${drawerTop}px` }"
    >
      <RouterLink
        v-for="l in primaryLinks"
        :key="l.key"
        :to="routeWithBrand(l.to)"
        class="appnav__link appnav__drawer-link"
        :class="{ 'appnav__link--active': primaryActive(l.routes) }"
        :aria-current="primaryActive(l.routes) ? 'location' : undefined"
        @click="onDrawerLinkClick"
      >{{ l.label }}</RouterLink>

      <RouterLink
        :to="routeWithBrand('/discovery')"
        class="appnav__link appnav__drawer-link"
        :class="{ 'appnav__link--active': analysisActive }"
        @click="onDrawerLinkClick"
      >วิเคราะห์</RouterLink>

      <section class="appnav__drawer-section" aria-labelledby="appnav-drawer-analysis-label">
        <h2 id="appnav-drawer-analysis-label" class="appnav__drawer-label">ในหมวดวิเคราะห์</h2>
        <RouterLink
          v-for="l in analysisLinks"
          :key="l.to"
          :to="routeWithBrand(l.to)"
          class="appnav__link appnav__drawer-link appnav__drawer-link--child"
          @click="onDrawerLinkClick"
        >{{ l.label }}</RouterLink>
      </section>

      <section class="appnav__drawer-section" aria-labelledby="appnav-drawer-tools-label">
        <h2 id="appnav-drawer-tools-label" class="appnav__drawer-label">เครื่องมือ</h2>
        <RouterLink
          v-for="l in toolLinks"
          :key="l.to"
          :to="routeWithBrand(l.to)"
          class="appnav__link appnav__drawer-link appnav__drawer-link--child"
          :class="{ 'appnav__link--active': toolsActive }"
          @click="onDrawerLinkClick"
        >{{ l.label }}</RouterLink>
      </section>

      <div v-if="showBrandSwitcher" class="appnav__brand appnav__drawer-brand" role="group" aria-label="เลือกแบรนด์">
        <button
          v-for="option in BRAND_LENSES"
          :key="option.value"
          type="button"
          class="appnav__brand-btn"
          :class="{ 'appnav__brand-btn--active': brand === option.value }"
          :aria-pressed="brand === option.value"
          @click="setBrand(option.value)"
        >{{ option.label }}</button>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.appnav {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.75rem;
  border-bottom: 1px solid var(--line);
  background: var(--surface);
  position: sticky;
  top: 0;
  z-index: 40;
}

/* Desktop grouping of the link row + brand switcher. Hidden on mobile, where
 * the same controls render inside the hamburger drawer instead. */
.appnav__inline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
}

/* Hamburger button: hidden on desktop; shown ≤720px. */
.appnav__burger {
  display: none;
}

/* Mobile drawer: hidden on desktop regardless of open state (mq listener
 * also closes it, this is the belt-and-suspenders). */
.appnav__drawer {
  display: none;
}

.appnav__link {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  min-height: 2.75rem;
  padding: 0.4rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-dim);
  text-decoration: none;
  border-radius: var(--radius-chip);
  font-family: 'Sarabun', sans-serif;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
}

.appnav__link:hover {
  background: var(--surface-2);
  color: var(--text);
}

.appnav__link--active {
  background: var(--brand-soft);
  color: var(--brand-light);
}

.appnav__group {
  position: relative;
  display: inline-flex;
  align-items: stretch;
}

.appnav__group-main {
  border-radius: var(--radius-chip) 0 0 var(--radius-chip);
}

.appnav__group-toggle,
.appnav__tools-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  border: 0;
  background: transparent;
  color: var(--text-dim);
  font: 600 0.75rem/1.2 'Sarabun', sans-serif;
  transition: background 0.15s ease, color 0.15s ease;
}

.appnav__group-toggle {
  width: 2.75rem;
  min-width: 2.75rem;
  padding: 0;
  border-left: 1px solid var(--line);
  border-radius: 0 var(--radius-chip) var(--radius-chip) 0;
}

.appnav__tools-btn {
  gap: 0.25rem;
  padding: 0.4rem 0.7rem;
  border-radius: var(--radius-chip);
}

.appnav__group-toggle:hover,
.appnav__tools-btn:hover,
.appnav__group-toggle--active,
.appnav__tools-btn--active {
  background: var(--brand-soft);
  color: var(--brand-light);
}

.appnav__group-panel {
  position: absolute;
  top: calc(100% + 0.3rem);
  left: 0;
  z-index: 80;
  display: grid;
  min-width: 12rem;
  padding: 0.35rem;
  border: 1px solid var(--line);
  border-radius: var(--radius-btn);
  background: var(--surface);
}

.appnav__group-panel--right {
  right: 0;
  left: auto;
}

.appnav__group-link {
  display: flex;
  align-items: center;
  min-height: 2.75rem;
  padding: 0.45rem 0.65rem;
  border-radius: var(--radius-badge);
  color: var(--text-dim);
  font: 600 0.76rem/1.3 'Sarabun', sans-serif;
  text-decoration: none;
  white-space: nowrap;
}

.appnav__group-link:hover,
.appnav__group-link.router-link-active {
  background: var(--brand-soft);
  color: var(--brand-light);
}

.appnav__spacer {
  flex: 1;
}

.appnav__brand {
  display: inline-flex;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: var(--radius-badge);
  background: var(--surface-2);
}
.appnav__brand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* 2.75rem = 44px minimum touch target (lucky-war-8bv2). In rem so the
   * hit area scales up with the user's font-size preference, never down. */
  min-height: 2.75rem;
  border: 0;
  border-right: 1px solid var(--line);
  padding: 0.35rem 0.75rem;
  background: transparent;
  color: var(--text-dim);
  font: 600 0.68rem/1.2 'Sarabun', sans-serif;
  white-space: nowrap;
}
.appnav__brand-btn:last-child { border-right: 0; }
.appnav__brand-btn--active { background: var(--brand-soft); color: var(--brand-light); }

/* ── Settings button + popover ───────────────────────────────────────────── */
.appnav__settings {
  position: relative;
  flex-shrink: 0;
  margin-left: 0.25rem;
}

.appnav__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  padding: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-chip);
  color: var(--text-dim);
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.appnav__icon-btn:hover {
  background: var(--surface-2);
  color: var(--text);
}
.appnav__icon-btn--active {
  background: var(--brand-soft);
  color: var(--brand-light);
  border-color: var(--line-light);
}

.settings {
  position: fixed;
  /* top + right are bound inline from the button's bounding rect */
  z-index: 1000;
  min-width: 14rem;
  padding: 0.75rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
  box-shadow: none;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.settings__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.settings__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-dim);
  white-space: nowrap;
}

.settings__seg {
  display: inline-flex;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
  overflow: hidden;
}

.settings__seg-btn {
  padding: 0.3rem 0.55rem;
  background: transparent;
  border: 0;
  color: var(--text-dim);
  font-size: 0.75rem;
  font-weight: 600;
  font-family: 'Sarabun', sans-serif;
  line-height: 1.2;
  transition: background 0.15s ease, color 0.15s ease;
}
.settings__seg-btn + .settings__seg-btn {
  border-left: 1px solid var(--line);
}
.settings__seg-btn:hover {
  color: var(--text);
}
.settings__seg-btn--active {
  background: var(--brand-soft);
  color: var(--brand-light);
}

/* ── Mobile (≤720px): collapse the inline row behind the hamburger ──────────
 * Kept after the base rules so the canonical .appnav__brand-btn block above
 * stays the first match for the touch-target contract test. */
@media (max-width: 720px) {
  .appnav__burger {
    display: inline-flex;
    /* 2.75rem = 44px minimum touch target (lucky-war-8bv2). */
    min-width: 2.75rem;
    min-height: 2.75rem;
  }

  .appnav__inline {
    display: none;
  }

  .appnav__drawer {
    display: flex;
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 90;
    flex-direction: column;
    align-items: stretch;
    gap: 0.15rem;
    padding: 0.35rem 0.75rem max(0.75rem, env(safe-area-inset-bottom));
    border-top: 1px solid var(--line);
    background: var(--surface);
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .appnav__drawer-link {
    padding: 0.55rem 0.7rem;
  }

  .appnav__drawer-section {
    display: grid;
    gap: 0.15rem;
    margin: 0.2rem 0 0;
    padding: 0.35rem 0 0;
    border-top: 1px solid var(--line);
  }

  .appnav__drawer-label {
    margin: 0;
    padding: 0.25rem 0.7rem 0.1rem;
    color: var(--text-muted);
    font: 600 0.66rem/1.3 'Sarabun', sans-serif;
  }

  .appnav__drawer-link--child {
    padding-left: 1.35rem;
  }

  .appnav__drawer-brand {
    margin-top: 0.35rem;
  }

  .appnav__drawer-brand .appnav__brand-btn {
    flex: 1;
  }
}
</style>
